<template>
  <div>
    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2 m-3"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div class="assets-grid-card">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-diagram-3-fill" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.anchorPlacement.sections.withScheme') }}
            </div>
          </div>
        </div>
        <div class="assets-grid-controls">
          <button
            type="button"
            class="assets-toolbar-btn"
            @click="goToCreate()"
          >
            <i class="bi bi-plus-circle" />
            <span>{{ $t('components.infrastructure.anchorPlacement.addScheme') }}</span>
          </button>
        </div>
      </div>

      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap">
          <input
            v-model="schemeSearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.anchorPlacement.searchPlaceholder')"
          >
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="dataLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            class="ni-grid"
            :data-source="filteredSchemeRows"
            key-expr="id"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :column-hiding-enabled="isNarrowViewport"
            :word-wrap-enabled="true"
            :no-data-text="$t('components.infrastructure.anchorPlacement.empty')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="name"
              :caption="$t('components.infrastructure.anchorPlacement.columns.name')"
              :min-width="160"
              :allow-sorting="false"
            />
            <DxColumn
              data-field="groupName"
              :caption="$t('components.infrastructure.anchorPlacement.columns.location')"
              :min-width="180"
              :allow-sorting="false"
              cell-template="location-template"
            />
            <DxColumn
              data-field="anchorMarkerCount"
              :caption="$t('components.infrastructure.anchorPlacement.columns.anchors')"
              alignment="center"
              width="150"
              :allow-sorting="false"
              :hiding-priority="1"
              cell-template="anchors-template"
            />
            <DxColumn
              data-field="assetMarkerCount"
              :caption="$t('components.infrastructure.anchorPlacement.columns.assets')"
              alignment="center"
              width="150"
              :allow-sorting="false"
              :hiding-priority="0"
              cell-template="assets-template"
            />
            <DxColumn
              :caption="$t('components.infrastructure.anchorPlacement.columns.actions')"
              width="110"
              :allow-filtering="false"
              :allow-sorting="false"
              cell-template="actions-template"
            />

            <template #location-template="{ data }">
              <span class="text-muted small">{{ data.data.groupName }}</span>
            </template>

            <template #anchors-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.anchorMarkerCount }} ({{ data.data.anchorAvailable }})</span>
            </template>

            <template #assets-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.assetMarkerCount }} ({{ data.data.assetAvailable }})</span>
            </template>

            <template #actions-template="{ data }">
              <div class="wl-row-actions">
                <button
                  type="button"
                  class="wl-row-action"
                  :title="$t('components.infrastructure.anchorPlacement.edit')"
                  @click.stop="openAnchorEditor(data.data)"
                >
                  <i class="bi bi-pencil-square" />
                </button>
                <button
                  type="button"
                  class="wl-row-action wl-row-action--danger"
                  :title="$t('components.infrastructure.anchorPlacement.deleteScheme')"
                  @click.stop="openDeleteConfirm(data.data)"
                >
                  <i class="bi bi-trash" />
                </button>
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <div class="assets-grid-card ni-grid-card--spaced">
      <div class="assets-grid-card__header">
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-geo-alt-fill" />
          </div>
          <div>
            <div class="assets-grid-title__text">
              {{ $t('components.infrastructure.anchorPlacement.sections.withoutScheme') }}
            </div>
          </div>
        </div>
      </div>

      <div class="assets-grid-card__body">
        <div class="wl-assets-search-wrap">
          <input
            v-model="locationSearch"
            type="text"
            class="form-control wl-assets-search"
            :placeholder="$t('components.infrastructure.anchorPlacement.searchLocationPlaceholder')"
          >
        </div>

        <div
          v-if="hasActivated"
          class="wp-grid-wrap"
        >
          <div
            v-if="dataLoading"
            class="wp-loading wp-loading--overlay"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.infrastructure.loading') }}</span>
          </div>

          <DxDataGrid
            class="ni-grid"
            :data-source="filteredLocationRows"
            key-expr="id"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :word-wrap-enabled="true"
            :no-data-text="$t('components.infrastructure.anchorPlacement.emptyLocations')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="10" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="name"
              :caption="$t('components.infrastructure.anchorPlacement.columns.locationName')"
              :min-width="220"
              :allow-sorting="false"
              cell-template="loc-name-template"
            />
            <DxColumn
              data-field="anchorAvailable"
              :caption="$t('components.infrastructure.anchorPlacement.columns.anchors')"
              alignment="center"
              width="150"
              :allow-sorting="false"
              :hiding-priority="1"
              cell-template="loc-anchors-template"
            />
            <DxColumn
              data-field="assetAvailable"
              :caption="$t('components.infrastructure.anchorPlacement.columns.assets')"
              alignment="center"
              width="150"
              :allow-sorting="false"
              :hiding-priority="0"
              cell-template="loc-assets-template"
            />
            <DxColumn
              :caption="$t('components.infrastructure.anchorPlacement.columns.actions')"
              width="90"
              :allow-filtering="false"
              :allow-sorting="false"
              cell-template="loc-actions-template"
            />

            <template #loc-name-template="{ data }">
              <span>{{ data.data.name }}</span>
              <span
                v-if="data.data.city"
                class="text-muted small"
              > · {{ data.data.city }}</span>
            </template>

            <template #loc-anchors-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.anchorSelected }} ({{ data.data.anchorAvailable }})</span>
            </template>

            <template #loc-assets-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.assetSelected }} ({{ data.data.assetAvailable }})</span>
            </template>

            <template #loc-actions-template="{ data }">
              <div class="wl-row-actions">
                <button
                  type="button"
                  class="wl-row-action"
                  :title="$t('components.infrastructure.anchorPlacement.edit')"
                  @click.stop="goToCreate(data.data)"
                >
                  <i class="bi bi-pencil-square" />
                </button>
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>
    </div>

    <base-modal
      ref="deleteModalRef"
      size="md"
      :center-top="true"
    >
      <template #header>
        <div class="assets-grid-title">
          <div class="assets-grid-title__icon">
            <i class="bi bi-exclamation-triangle-fill" />
          </div>
          <div class="assets-grid-title__text">
            {{ $t('components.infrastructure.anchorPlacement.confirmDeleteTitle') }}
          </div>
        </div>
      </template>

      <template #body>
        <div
          v-if="deleteError"
          class="alert alert-danger d-flex align-items-center gap-2 mb-3"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ deleteError }}</span>
        </div>
        <p class="mb-0">
          {{ schemeToDelete ? $t('components.infrastructure.anchorPlacement.confirmDeleteScheme', { name: schemeToDelete.name }) : '' }}
        </p>
      </template>

      <template #footer>
        <div class="d-flex justify-content-between w-100">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeDeleteConfirm"
          >
            {{ $t('components.assetGroupsWithLocation.modal.close') }}
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting"
            @click="confirmDeleteScheme"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t('components.infrastructure.anchorPlacement.confirmDeleteButton') }}
          </button>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxLoadPanel,
} from 'devextreme-vue/data-grid'
import BaseModal from '@/components/content/_modals/core/BaseModal.vue'
import { getAssetSchemes } from '@/crud/asset-scheme'
import { getAssetGroupsWithLocation, getAssetGroups, deleteAssetGroupScheme } from '@/crud/asset-group'
import { getWirepasGatewaysAdmin, getWirepasAnchors } from '@/crud/wirepas-location'
import { useNarrowViewport } from '@/composables/useNarrowViewport'

export default defineComponent({
  name: 'WirepasAnchorPlacementGrid',
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxLoadPanel, BaseModal },
  props: {
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const { t } = useI18n()
    const router = useRouter()

    const error = ref<string | null>(null)
    const dataLoading = ref(true)
    const schemeSearch = ref('')
    const locationSearch = ref('')
    const allGroups = ref<any[]>([])
    const allSchemes = ref<any[]>([])
    const assetGroupsFull = ref<any[]>([])
    const gatewaysAdmin = ref<any[]>([])
    const anchorsAdmin = ref<any[]>([])

    const deleteModalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const schemeToDelete = ref<{ id: number; groupId: number | null; name: string } | null>(null)
    const deleting = ref(false)
    const deleteError = ref<string | null>(null)

    const isNarrowViewport = useNarrowViewport()

    const schemeRows = computed(() => {
      return allSchemes.value
        .map((scheme: any) => {
          const markers = Array.isArray(scheme.markersJson) ? scheme.markersJson : []
          const anchorMarkerCount = markers.reduce((n: number, m: any) => n + (m.type === 'anchor' ? 1 : 0), 0)
          const groupId = scheme.group?.id ?? null
          return {
            id: scheme.id,
            name: scheme.name,
            groupId,
            groupName: scheme.group?.name || '-',
            anchorMarkerCount,
            assetMarkerCount: markers.length - anchorMarkerCount,
            // "Placed (available)" -- e.g. 6 (26): 6 anchors on the canvas out of 26 the group's
            // gateway actually hears.
            anchorAvailable: groupId != null ? (anchorCountByGroupId.value.get(groupId) ?? 0) : 0,
            assetAvailable: groupId != null ? (assetsCountByGroupId.value.get(groupId) ?? 0) : 0,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
    })

    const filteredSchemeRows = computed(() => {
      const term = schemeSearch.value.trim().toLowerCase()
      if (!term) return schemeRows.value
      return schemeRows.value.filter(row => row.name.toLowerCase().includes(term) || row.groupName.toLowerCase().includes(term))
    })

    const schemedGroupIds = computed(() => new Set(allSchemes.value.map((s: any) => s.group?.id).filter((id: any) => id != null)))

    // How many assets belong to the group, and how many Wirepas anchors are heard by its
    // gateway(s) -- i.e. what would show up in the editor's picker once a scheme exists here.
    const assetsCountByGroupId = computed(() => {
      const map = new Map<number, number>()
      for (const g of assetGroupsFull.value) map.set(g.id, g.assetsCount ?? 0)
      return map
    })

    const gatewayIdsByGroupId = computed(() => {
      const map = new Map<number, string[]>()
      for (const gw of gatewaysAdmin.value) {
        if (gw.assetGroupId == null) continue
        const list = map.get(gw.assetGroupId) || []
        list.push(gw.gwId)
        map.set(gw.assetGroupId, list)
      }
      return map
    })

    const anchorCountByGroupId = computed(() => {
      const map = new Map<number, number>()
      for (const [groupId, gwIds] of gatewayIdsByGroupId.value) {
        const gwSet = new Set(gwIds)
        map.set(groupId, anchorsAdmin.value.filter((a: any) => (a.gateways || []).some((gw: string) => gwSet.has(gw))).length)
      }
      return map
    })

    const locationRows = computed(() => {
      return allGroups.value
        .filter((g: any) => !schemedGroupIds.value.has(g.id))
        .map((g: any) => ({
          id: g.id,
          name: g.name,
          city: g.location?.city || null,
          // No scheme yet, so nothing is placed -- 0 (available), same "placed (available)"
          // format as the other table for consistency.
          anchorSelected: 0,
          anchorAvailable: anchorCountByGroupId.value.get(g.id) ?? 0,
          assetSelected: 0,
          assetAvailable: assetsCountByGroupId.value.get(g.id) ?? 0,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
    })

    const filteredLocationRows = computed(() => {
      const term = locationSearch.value.trim().toLowerCase()
      if (!term) return locationRows.value
      return locationRows.value.filter(row => row.name.toLowerCase().includes(term) || (row.city || '').toLowerCase().includes(term))
    })

    const fetchAllData = async () => {
      dataLoading.value = true
      error.value = null
      try {
        const [groupsRes, schemesRes, assetGroupsRes, gatewaysRes, anchorsRes] = await Promise.all([
          getAssetGroupsWithLocation({ take: 1000 }),
          getAssetSchemes({ take: 1000 }),
          getAssetGroups({ take: 1000 }),
          getWirepasGatewaysAdmin({ take: 1000 }),
          getWirepasAnchors({ take: 1000, includeTagsHeard: false }),
        ])
        allGroups.value = groupsRes.data?.data?.result || []
        allSchemes.value = schemesRes.data?.result || []
        assetGroupsFull.value = assetGroupsRes.data?.data?.result || []
        gatewaysAdmin.value = gatewaysRes.data?.result || []
        anchorsAdmin.value = anchorsRes.data?.result || []
      } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
      } finally {
        dataLoading.value = false
      }
    }

    // Renders only after first activation -- all Infrastructure tabs mount together via v-show.
    const hasActivated = ref(props.active)

    // Fetches once per tab activation; re-fetched again after any add/delete scheme action.
    watch(() => props.active, (isActive) => {
      if (isActive) {
        hasActivated.value = true
        fetchAllData()
      }
    }, { immediate: true })

    const openAnchorEditor = (row: { id: number }) => {
      router.push({ name: 'AssetSchemeEdit', params: { id: row.id }, query: { mode: 'anchor' } })
    }

    const openDeleteConfirm = (row: { id: number; groupId: number | null; name: string }) => {
      schemeToDelete.value = row
      deleteError.value = null
      deleteModalRef.value?.openModal()
    }

    const closeDeleteConfirm = () => {
      deleteModalRef.value?.closeModal()
    }

    const confirmDeleteScheme = async () => {
      const row = schemeToDelete.value
      if (!row || !row.groupId) return
      deleting.value = true
      deleteError.value = null
      try {
        await deleteAssetGroupScheme(row.groupId, row.id)
        closeDeleteConfirm()
        schemeToDelete.value = null
        await fetchAllData()
      } catch (e: any) {
        deleteError.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
      } finally {
        deleting.value = false
      }
    }

    // Creating a scheme happens inside the same editor as managing one -- it shows a blank
    // rectangle plus the create form until the scheme is actually saved. groupId/groupName
    // preselect the location when coming from a specific "bez schematu" row.
    const goToCreate = (row: { id: number; name: string } | null = null) => {
      router.push({
        name: 'AssetSchemeEdit',
        params: { id: 'new' },
        query: row ? { mode: 'anchor', groupId: String(row.id), groupName: row.name } : { mode: 'anchor' },
      })
    }

    return {
      error,
      hasActivated,
      dataLoading,
      schemeSearch,
      locationSearch,
      filteredSchemeRows,
      filteredLocationRows,
      isNarrowViewport,
      deleteModalRef,
      schemeToDelete,
      deleting,
      deleteError,
      goToCreate,
      openAnchorEditor,
      openDeleteConfirm,
      closeDeleteConfirm,
      confirmDeleteScheme,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

// Search box sits flush against the grid below it otherwise (.wl-assets-search-wrap has no
// bottom padding) -- add a little breathing room, scoped to this tab only.
.wl-assets-search-wrap {
  margin-bottom: 10px;
}
</style>
